import {
  BlockStack,
  Card,
  FormLayout,
  InlineStack,
  Select,
  Text,
  TextField,
} from '@shopify/polaris';
import type { ScheduleType } from '@/queries/notifications';
import type { SendMode, TriggerKind } from '../shared/types';

const SEND_MODE_OPTIONS: { label: string; value: SendMode }[] = [
  { label: 'ابعت دلوقتي', value: 'now' },
  { label: 'جدول لوقت معين (One-time)', value: 'one_time' },
  { label: 'متكرر (Recurring — cron)', value: 'recurring' },
  { label: 'Trigger-based (حسب حدث)', value: 'trigger' },
];

const CRON_PRESETS: { label: string; value: string }[] = [
  { label: 'كل جمعة 9 صباحاً (UTC)', value: '0 9 * * 5' },
  { label: 'كل يوم 6 مساءً (UTC)', value: '0 18 * * *' },
  { label: 'كل ساعة', value: '0 * * * *' },
  { label: 'Custom', value: '__custom__' },
];

const TRIGGER_OPTIONS: { label: string; value: TriggerKind }[] = [
  { label: 'بعد install بـ X ساعة', value: 'install_plus_hours' },
  { label: 'بعد signup مباشرة', value: 'signup' },
  { label: 'Cart مهجور لـ X ساعة', value: 'cart_idle' },
  { label: 'منتج في الكارت نزل سعره', value: 'price_drop' },
];

export interface ScheduleCardProps {
  sendMode: SendMode;
  scheduleAt: string;
  cronExpr: string;
  triggerKind: TriggerKind;
  triggerHours: string;
  abEnabled: boolean;
  onSendModeChange: (v: SendMode) => void;
  onScheduleAtChange: (v: string) => void;
  onCronExprChange: (v: string) => void;
  onTriggerKindChange: (v: TriggerKind) => void;
  onTriggerHoursChange: (v: string) => void;
}

export function ScheduleCard(props: ScheduleCardProps) {
  const {
    sendMode,
    scheduleAt,
    cronExpr,
    triggerKind,
    triggerHours,
    abEnabled,
    onSendModeChange,
    onScheduleAtChange,
    onCronExprChange,
    onTriggerKindChange,
    onTriggerHoursChange,
  } = props;

  // A/B mode locks scheduling to "now"
  const options = abEnabled
    ? SEND_MODE_OPTIONS.filter((o) => o.value === 'now')
    : SEND_MODE_OPTIONS;

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h2" variant="headingMd">
            التوقيت
          </Text>
          <Text as="span" variant="bodySm" tone="subdued">
            الخطوة 4 من 4
          </Text>
        </InlineStack>

        <FormLayout>
          <Select
            label="إمتى يتبعت؟"
            options={options}
            value={sendMode}
            onChange={(v) => onSendModeChange(v as SendMode)}
            helpText={abEnabled ? 'A/B testing مدعوم فقط لـ Send now' : undefined}
            disabled={abEnabled}
          />

          {sendMode === 'one_time' ? (
            <TextField
              label="وقت الإرسال (وقتك المحلي)"
              type="datetime-local"
              value={scheduleAt}
              onChange={onScheduleAtChange}
              autoComplete="off"
            />
          ) : null}

          {sendMode === 'recurring' ? (
            <>
              <Select
                label="جدول جاهز"
                options={CRON_PRESETS}
                value={
                  CRON_PRESETS.some((p) => p.value === cronExpr) ? cronExpr : '__custom__'
                }
                onChange={(v) => {
                  if (v !== '__custom__') onCronExprChange(v);
                }}
              />
              <TextField
                label="Cron expression (UTC)"
                value={cronExpr}
                onChange={onCronExprChange}
                autoComplete="off"
                helpText="مثل: 0 9 * * 5 = كل جمعة 9 صباحاً UTC"
              />
            </>
          ) : null}

          {sendMode === 'trigger' ? (
            <>
              <Select
                label="الحدث"
                options={TRIGGER_OPTIONS}
                value={triggerKind}
                onChange={(v) => onTriggerKindChange(v as TriggerKind)}
              />
              {triggerKind !== 'signup' && triggerKind !== 'price_drop' ? (
                <TextField
                  label="عدد الساعات"
                  type="number"
                  value={triggerHours}
                  onChange={onTriggerHoursChange}
                  autoComplete="off"
                />
              ) : null}
            </>
          ) : null}
        </FormLayout>
      </BlockStack>
    </Card>
  );
}

// Re-export ScheduleType for compatibility
export type { ScheduleType };
